import {
    AfterViewInit,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { ITextPanelButton, TextPanelBase } from './text-panel-base';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { ObjectTypes } from '@fi-shared/types';
import { AdvancedTextPanelHistoryService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-history.service';
import { AdvancedTextPanelMediaLinkService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-media-link.service';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';
import { takeUntil } from 'rxjs/operators';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { TextEditorInsertMediaService } from '@fi-shared/text-editor/text-editor-insert-media.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { AdvancedTextPanelFontStyleService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-font-style.service';
import { AdvancedTextPanelListsService } from '@fi-shared/fi-text-panel/text-panel-services/advanced-text-panel-lists.service';
import { TextEditorAttachmentType } from '@fi-app/shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { ITextEditorActiveButtons, ITextEditorLinkAttachment } from '@fi-app/shared/fi-text-editor/fi-text-editor';
import { TextEditorInsertEmbedVideoService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-embed-video/text-editor-insert-embed-video.service';
import { BehaviorSubject } from 'rxjs';
import { FiTextPanelHelperService } from '../text-panel-services/fi-text-panel-helper.service';

@Component({
    selector: 'fi-simple-text-panel',
    templateUrl: './simple-text-panel.component.html',
    styleUrls: ['./simple-text-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-simple-text-panel' },
    standalone: false,
})
export class SimpleTextPanelComponent extends TextPanelBase implements OnInit, AfterViewInit, OnDestroy {
    @Input() closeInsertMediaDialogAfterFileSelection: boolean;
    @Input() componentElement: HTMLElement;
    @Input() objectType: ObjectTypes;

    @Input() isTagUserFeatureEnabled: boolean = false;
    @Input() isEmbedVideoFeatureEnabled: boolean = false;
    @Input() isGifFeatureEnabled: boolean = false;
    @Input() isInsertMediaEnabled: boolean = true;
    @Input() isInsertLinkEnabled: boolean = true;
    @Input() isInternalLinksAllowed: boolean = true;

    @Input() preventInsertingMediaIntoTextEditor: boolean;
    public onInsertMedia = output<IMediaBankFileReference[]>();
    public onInsertLinkAttachment = output<ITextEditorLinkAttachment>();
    public formattingChanged = output<void>();

    private textEditorInsertMediaService = inject(TextEditorInsertMediaService);
    private insertMedia2Service = inject(FiInsertMedia2Service);
    private advancedTextPanelHistoryService = inject(AdvancedTextPanelHistoryService);
    private advancedTextPanelMediaLinkService = inject(AdvancedTextPanelMediaLinkService);
    private advancedTextHandlerService = inject(AdvancedTextHandlerService);
    private advancedTextPanelFontStyleService = inject(AdvancedTextPanelFontStyleService);
    private advancedTextPanelListsService = inject(AdvancedTextPanelListsService);
    private textEditorInsertEmbedVideoService = inject(TextEditorInsertEmbedVideoService);
    private fiTextPanelHelperService = inject(FiTextPanelHelperService);

    protected historyButtons: ITextPanelButton[] = this.advancedTextPanelHistoryService.getButtons();
    protected listButtons: ITextPanelButton[] = this.advancedTextPanelListsService.getButtons();
    protected textDecorationButtons: ITextPanelButton[] =
        this.advancedTextPanelFontStyleService.getSimpleTextFontStyleButtons(
            this.selectedButtonsData,
            this.changeTextStyles.bind(this),
        );
    protected linkButton: ITextPanelButton;

    protected textEditorOpenedDialogs: ITextEditorActiveButtons = {};
    protected isGiphyOverlayShown$ = new BehaviorSubject<boolean>(false);

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.setLinkButtons();
        this.subscribeToClickEvent();
    }

    public ngAfterViewInit(): void {
        this.subscribeToScrollUpdating();
    }

    private subscribeToScrollUpdating(): void {
        const panelElement: HTMLElement = this.elementRef.nativeElement;
        const wrapperElement: HTMLElement =
            this.getEditorElement() ?? (panelElement.offsetParent as HTMLElement) ?? panelElement.parentElement;

        if (!wrapperElement) {
            return;
        }

        this.fiTextPanelHelperService.subscribeToScrollUpdating(
            wrapperElement,
            panelElement,
            0,
            this.ngOnDestroy$,
        );
    }

    private setLinkButtons(): void {
        this.linkButton = this.advancedTextPanelMediaLinkService
            .getButtons(undefined, this.isInternalLinksAllowed)
            .find((button: ITextPanelButton) => {
                return button.isMatMenu;
            });
    }

    private subscribeToClickEvent(): void {
        this.advancedTextHandlerService.onClick$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((event: MouseEvent) => {
            this.currentElement = event.target as HTMLElement;
        });
    }

    private getEditorElement(): HTMLElement {
        const element: HTMLElement = this.componentElement ?? this.elementRef.nativeElement;
        if (element.matches('.text-editor')) {
            return element;
        }
        return element.parentElement?.querySelector('.text-editor');
    }

    private _isAttachmentDialogOpen: boolean = false;
    protected get isAttachmentDialogOpen(): boolean {
        return this._isAttachmentDialogOpen;
    }

    private openSimpleInsertMediaDialog(): void {
        this._isAttachmentDialogOpen = true;
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(this.objectType)
            .onYesButtonClick((result: IInsertMediaResult) => {
                this.onInsertMedia.emit(result.mediaBankFileReferences);
            })
            .afterClosed(() => (this._isAttachmentDialogOpen = false));
    }

    private openInsertMediaInSelectionDialog(): void {
        const selection: Selection = window.getSelection();
        const editableElement: HTMLElement = this.getEditableElementFromSelection(selection.anchorNode);
        const range: Range = this.textEditorInsertMediaService
            .getRangeForMediaInsert(editableElement, selection)
            .cloneRange();

        this._isAttachmentDialogOpen = true;
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(this.objectType)
            .onYesButtonClick((result: IInsertMediaResult) => {
                range.deleteContents();
                this.textEditorInsertMediaService
                    .createNodesFromResult(result.mediaBankFileReferences)
                    .then((nodes: HTMLElement[]) => {
                        this.textEditorInsertMediaService.insertMediaIntoTextEditorRange(range, selection, nodes);
                    });
            })
            .afterClosed(() => (this._isAttachmentDialogOpen = false));
    }

    protected insertMedia(): void {
        if (this.preventInsertingMediaIntoTextEditor) {
            this.openSimpleInsertMediaDialog();
            return;
        }

        this.openInsertMediaInSelectionDialog();
    }

    protected insertTag(): void {
        const contentEditableElem = this.getEditorElement();
        this.fiTextPanelHelperService.insertTag(contentEditableElem);
    }

    protected insertEmbedVideo(): void {
        const contentEditableElem = this.getEditorElement();
        this.textEditorOpenedDialogs.embed = true;
        this.textEditorInsertEmbedVideoService.openInsertEmbedVideoDialog(undefined, {
            onYesButtonClick: (attachment) => {
                this.onInsertLinkAttachment.emit(attachment);
            },
            onNoButtonClick: () => contentEditableElem.focus(),
            afterClosed: () => (this.textEditorOpenedDialogs.embed = false),
        });
    }

    protected toggleGiphyOverlay(isShown: boolean): void {
        setTimeout(() => this.isGiphyOverlayShown$.next(isShown));
    }

    protected onGiphyItemClick($event: ITextEditorLinkAttachment): void {
        this.toggleGiphyOverlay(false);
        this.onInsertLinkAttachment.emit($event);
    }

    private getEditableElementFromSelection(anchorNode: Node): HTMLElement {
        const editorElement: HTMLElement = this.getEditorElement();
        const selectionAnchorNode: Node = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentNode : anchorNode;

        if (isNullOrUndefined(editorElement) && (selectionAnchorNode as HTMLElement)?.matches('.text-editor')) {
            return selectionAnchorNode as HTMLElement;
        }
        return editorElement;
    }

    protected textDecorationButtonClick(button: ITextPanelButton, $event: MouseEvent): void {
        this.getEditorElement().focus();
        button.callback();
        $event.stopPropagation();
        this.formattingChanged.emit();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
